import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';

import { denormalize } from '../shared/normalizers';

import { Trainer, TransferResponse } from './_types';
import { normalizeTrainer } from './normalizers';
import { parseHqAssignmentValue } from './utils';

// Re-export types for convenience
export type { Trainer, TransferResponse };

export const fetchCurrentTrainer = async (): Promise<Trainer> => {
  const response = await api.get(`/api/v4/trainers/current`);
  return normalizeTrainer(response.data);
};

export const fetchAllTrainers = async (
  params: Record<string, any> = {}
): Promise<Trainer[]> => {
  const response = await api.get(`/api/v3/trainers`, {
    params: {
      fetch_all: true,
      ...params,
    },
  });
  return response.data.map((t: any) => normalizeTrainer(t));
};

export interface FetchTrainersOptions {
  page?: number;
  per?: number;
  search?: string;
  sortBy?: string;
  sortDir?: string;
}

export interface FetchTrainersResponse {
  trainers: Trainer[];
  total: number;
}

export const fetchTrainers = async ({
  page = 1,
  per = 10,
  search = '',
  sortBy = '',
  sortDir = '',
}: FetchTrainersOptions): Promise<FetchTrainersResponse> => {
  const response = await api.get(`/api/v3/trainers`, {
    params: {
      page,
      per,
      'q[trainer_nickname_or_trainer_first_name_or_trainer_last_name_or_trainer_email_cont]':
        search,
      'q[s]': sortBy ? `${decamelize(sortBy || '')} ${sortDir || ''}` : '',
    },
  });
  return {
    trainers: response.data.trainer.map((t: any) => normalizeTrainer(t)),
    total: response.data?.meta?.total,
  };
};

export interface DeleteTrainerOptions {
  trainerId: Trainer['id'];
  deleteAccount?: boolean;
}

export const deleteTrainer = async ({
  trainerId,
  deleteAccount = false,
}: DeleteTrainerOptions): Promise<Trainer> => {
  const response = await api.delete(`/api/v3/trainers/${trainerId}`, {
    data: { delete_account: deleteAccount },
  });
  return normalizeTrainer(response.data);
};

export interface FetchTrainerOptions {
  id: Trainer['id'];
}

export const fetchTrainer = async ({
  id,
}: FetchTrainerOptions): Promise<Trainer> => {
  const response = await api.get(`/api/v4/users/${id}`);
  return normalizeTrainer(response.data);
};

export const transferToTrainer = async (
  trainerId: Trainer['id'],
  amount: number,
  memo: string
): Promise<TransferResponse> => {
  const response = await api.post(`/api/v4/trainers/${trainerId}/transfer_to`, {
    amount,
    memo,
  });

  return response.data;
};

export const createTrainer = async (trainer: Trainer): Promise<Trainer> => {
  const response = await api.post(`/api/v3/trainers`, {
    trainer: denormalize(trainer),
  });
  return normalizeTrainer(response.data);
};

export interface FetchTrainerByTrainerIdOptions {
  trainerId: Trainer['id'];
}

export const fetchTrainerByTrainerId = async ({
  trainerId,
}: FetchTrainerByTrainerIdOptions): Promise<Trainer> => {
  const response = await api.get(`/api/v3/trainers/${trainerId}`);
  return normalizeTrainer(response.data);
};

export const updateTrainer = async (
  trainerId: Trainer['id'],
  trainer: Partial<Trainer>
): Promise<Trainer> => {
  // Remove id from trainer object before sending to API
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, ...trainerWithoutId } = trainer;

  const response = await api.put(`/api/v3/trainers/${trainerId}`, {
    trainer: {
      ...(denormalize(trainerWithoutId) as Record<string, any>),
      tag_list: trainer.tags?.join(',') || '',
      landing_items: trainer.landingItems,
    },
  });
  return normalizeTrainer(response.data);
};

export const resendTrainerInvite = async (
  trainerId: Trainer['id']
): Promise<void> => {
  await api.post(`/api/v3/trainers/${trainerId}/resend_invite`);
};

export const updateTrainerHqAssignment = async (
  trainerId: Trainer['id'],
  hqAssignmentValue: string
): Promise<Trainer> => {
  const { hqAssignmentType, hqAssignmentId } =
    parseHqAssignmentValue(hqAssignmentValue);

  return updateTrainer(trainerId, {
    hqAssignmentType,
    hqAssignmentId,
  });
};
